import { PasteIcon } from "@images/svgIcons/pasteIcon"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { BlockItemTag } from "@modules/sessionBlock/components/blockItemTag"
import { ExerciseFormikType } from "@modules/sessionBlock/exercises/exerciseFormikTypes"
import { ExerciseCategories } from "@modules/sessionBlock/exercises/exerciseTypes"
import { getDisplayExerciseCategory } from "@modules/sessionBlock/exercises/exerciseUtils"
import { SessionBlockType } from "@modules/sessionBlock/sessionBlockTypes"
import { useSessionFormContext } from "@modules/sessions/hooks/useSessionFormContext"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { HURichTextInput } from "@modules/ui/components/richText/huRichTextInput"
import { formatTextToRichText } from "@modules/ui/components/richText/richTextUtils"
import { MenuItem } from "primereact/menuitem"
import { useMemo } from "react"
import { useTheme } from "styled-components"

export const ExerciseDraftForm: React.FC = () => {
  const { formik, updatedBlockIndex, isEditingBlock } = useSessionFormContext()
  const theme = useTheme()
  const toast = useToast()
  const { sessionService } = useServicesContext()
  const copyExercise = sessionService.getCopyExercise()

  const exercisesCategory = useMemo(
    () =>
      ExerciseCategories.map((exerciseCategory) => {
        return { value: exerciseCategory, label: getDisplayExerciseCategory(exerciseCategory) }
      }),
    [],
  )

  if (updatedBlockIndex === null) return <></>

  const formikNameBase = `blocks[${updatedBlockIndex}].content`
  const formikValue = formik.values.blocks[updatedBlockIndex].content as ExerciseFormikType

  const ellipsisItems: MenuItem[] = [
    {
      items: [
        {
          label: word("program.exercise.paste.label"),
          icon: <PasteIcon color={theme.neutralColor900} />,
          command: () => {
            onExercisePaste()
          },
          disabled: !copyExercise,
        },
      ],
    },
  ]

  const onExercisePaste = () => {
    try {
      if (copyExercise) {
        const newBlock: ExerciseFormikType = {
          category: copyExercise.category,
          otherCategory: copyExercise.otherCategory,
          chrono: copyExercise.chrono,
          chronoAmrap: copyExercise.chronoAmrap,
          chronoEmom: copyExercise.chronoEmom,
          chronoForTime: copyExercise.chronoForTime,
          chronoTabata: copyExercise.chronoTabata,
          chronoType: copyExercise.chrono?.type ?? "NONE",
          description: copyExercise.description,
          id: null,
          scoreType: copyExercise.scoreType,
          rpe: copyExercise.rpe,
          requiredMedia: copyExercise.requiredMedia,
          title: "",
        }
        const newBlocks = [...formik.values.blocks]
        newBlocks[updatedBlockIndex] = {
          content: newBlock,
          type: SessionBlockType.SESSION_EXERCISE,
        }

        formik.setFieldValue("blocks", newBlocks)
      }

      toast.show({
        severity: "success",
        summary: word("program.exercise.paste.success"),
      })
    } catch (e: any) {
      toast.show({
        severity: "error",
        summary: word("program.exercise.paste.error"),
      })
    }
  }

  return (
    <div className="flex flex-column gap-5 session-block-form">
      {/* Header */}
      <div className="flex align-items-center">
        <div className="flex flex-column gap-2" style={{ maxWidth: "70%" }}>
          <HUText fontStyle="LS" color={theme.neutralColor700} className="uppercase">
            {`(${updatedBlockIndex + 1}) ${word("program.planning.create.exercise.block_plural", { count: updatedBlockIndex })}`}
          </HUText>

          <HUText fontStyle="HS">
            {isEditingBlock
              ? word("program.planning.edit.exercise.title")
              : word("program.planning.create.exercise.title")}
          </HUText>

          <HUText fontStyle="BS" color={theme.neutralColor600}>
            {word("program.planning.create.exercise.description")}
          </HUText>
        </div>
        {copyExercise && (
          <div className="flex flex-1 justify-content-end gap-2">
            <EllipsisMenu items={ellipsisItems} />
          </div>
        )}
      </div>

      {/* Exercise category */}
      <div className="card p-3 mb-0">
        <HUText type="label" fontStyle="LS" className="uppercase">
          {word("sessionForm.exercise.category.label")}
          <HUText fontStyle="LL" color={theme.necessaryInput}>
            {" *"}
          </HUText>
        </HUText>

        <div className="mt-2 flex flex-row align-items-center flex-wrap gap-2">
          {exercisesCategory.map((category) => (
            <BlockItemTag
              key={category.value}
              label={category.label}
              isSelected={formikValue.category === category.value}
              onSelect={() => {
                formik.setFieldValue(`${formikNameBase}.category`, category.value)
              }}
              isSelectable
            />
          ))}
        </div>
        {getFormErrorMessage(`${formikNameBase}.category`, formik)}

        {formikValue.category === "OTHER" && (
          <FormikInput
            flex
            formikStyle={{ flexGrow: 1, borderRadius: 30, marginTop: "0.5rem" }}
            isRequiredInput
            placeHolder={word("sessionForm.exercise.category.other.placeHolder")}
            getFieldProps={formik.getFieldProps}
            setFieldValue={formik.setFieldValue}
            isInvalid={isFormFieldInValid(`${formikNameBase}.otherCategory`, formik)}
            error={getFormErrorMessage(`${formikNameBase}.otherCategory`, formik)}
            name={`${formikNameBase}.otherCategory`}
          />
        )}
      </div>

      {/* Exercise duration */}
      <div className="card p-3 mb-0">
        <FormikInput
          flex
          formikStyle={{ display: "flex", flexGrow: 1 }}
          label={word("sessionForm.exercise.duration.label")}
          description={word("sessionForm.exercise.duration.description")}
          getFieldProps={formik.getFieldProps}
          setFieldValue={formik.setFieldValue}
          isInvalid={isFormFieldInValid(`${formikNameBase}.durationInMin`, formik)}
          error={getFormErrorMessage(`${formikNameBase}.durationInMin`, formik)}
          name={`${formikNameBase}.durationInMin`}
          type="number"
          numberProps={{
            placeholder: "0",
            min: 0,
            max: 600,
            maxFractionDigits: 0,
          }}
          rightButton={
            <HUText fontStyle="TM">
              {word("global.minute.shortLabel", {
                count: formik.getFieldProps(`${formikNameBase}.durationInMin`).value ?? 1,
              })}
            </HUText>
          }
        />
      </div>

      {/* Exercise content */}
      <div className="card p-3 mb-0">
        <HURichTextInput
          value={formatTextToRichText(formikValue.description)}
          onChange={(value) => {
            formik.setFieldValue(`${formikNameBase}.description`, value)
          }}
          label={word("sessionForm.exercise.description.label")}
          isRequiredInput
          maxLength={65000}
          paragrapheFontStyle="BM"
          isInvalid={isFormFieldInValid(`${formikNameBase}.description`, formik)}
          error={getFormErrorMessage(`${formikNameBase}.description`, formik)}
          placeholder={word("program.planning.create.exercise.placeholder")}
        />
      </div>
    </div>
  )
}
